import { useEffect } from 'react';

export interface CanonicalBrowseState {
  region?: string;
  locality?: string;
  date?: string;
  month?: string;
  q?: string;
  category?: string;
  sort?: string;
  view?: string;
  display?: string;
}

const CANONICAL_KEYS = [
  'region',
  'locality',
  'date',
  'month',
  'q',
  'category',
  'sort',
  'view',
  'display',
] as const;

export function readCanonicalBrowseState(search: string): CanonicalBrowseState {
  const params = new URLSearchParams(search);
  return CANONICAL_KEYS.reduce<CanonicalBrowseState>((state, key) => {
    const value = params.get(key);
    if (value) {
      state[key] = value;
    }
    return state;
  }, {});
}

export function buildBrowseHref(pathname: string, state: CanonicalBrowseState): string {
  const params = new URLSearchParams();

  for (const key of CANONICAL_KEYS) {
    const value = state[key];
    if (value) {
      params.set(key, value);
    }
  }

  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
}

export function updateBrowseUrl(pathname: string, state: CanonicalBrowseState, replace = true) {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = buildBrowseHref(pathname, state);
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method](window.history.state, '', nextUrl);
}

export function buildReturnToTarget(pathname: string, search: string) {
  const currentSearch = search.startsWith('?') ? search : search ? `?${search}` : '';
  return `${pathname}${currentSearch}`;
}

function getScrollStorageKey(pathname: string, search: string) {
  return `kalovate.browse-scroll:${pathname}:${search}`;
}

export function useBrowseScrollRestoration(pathname: string, search: string, ready = true) {
  useEffect(() => {
    if (typeof window === 'undefined' || !ready) {
      return;
    }

    const storageKey = getScrollStorageKey(pathname, search);
    const storedY = window.sessionStorage.getItem(storageKey);

    if (storedY) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: Number(storedY), left: 0, behavior: 'auto' });
      });
      window.sessionStorage.removeItem(storageKey);
    }

    const persistScroll = () => {
      window.sessionStorage.setItem(storageKey, String(window.scrollY));
    };

    window.addEventListener('pagehide', persistScroll);
    return () => {
      persistScroll();
      window.removeEventListener('pagehide', persistScroll);
    };
  }, [pathname, ready, search]);
}
